'use client';

import { useState } from 'react';
import Link from 'next/link';
import { 
  Home, 
  Users, 
  Activity, 
  MessageCircle, 
  Search, 
  Star, 
  Crown,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { DAILY_RECOMMENDATION_MENU_NAME } from '@/utils/dailyRecommendationMenu';
import DailyRecommendationMenuLabel from './DailyRecommendationMenuLabel';
import IDSearchPopup from './IDSearchPopup';
import SurnameSearchPopup from './SurnameSearchPopup';

interface NavigationProps {
  className?: string;
}

interface DropdownItem {
  name: string;
  href?: string;
  action?: string;
}

export default function Navigation({ className = '' }: NavigationProps) {
  const { premium } = useAuth();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showIDSearch, setShowIDSearch] = useState(false);
  const [showSurnameSearch, setShowSurnameSearch] = useState(false);

  const toggleDropdown = (menuName: string) => {
    setActiveDropdown(activeDropdown === menuName ? null : menuName);
  };

  const handleSearchOptionClick = (searchType: string) => {
    setActiveDropdown(null);
    if (searchType === 'id') {
      setShowIDSearch(true);
    } else if (searchType === 'surname') {
      setShowSurnameSearch(true);
    }
  };

  const navigationItems: Array<{
    name: string;
    icon?: React.ComponentType<{ className?: string }>;
    href?: string;
    hasDropdown: boolean;
    isDailyRecommendation?: boolean;
    dropdownItems?: DropdownItem[];
  }> = [
    {
      name: 'Home',
      icon: Home,
      href: '/dashboard',
      hasDropdown: false
    },
    {
      name: DAILY_RECOMMENDATION_MENU_NAME,
      href: '/daily-recommendations',
      hasDropdown: false,
      isDailyRecommendation: true,
    },
    {
      name: 'Matches',
      icon: Users,
      hasDropdown: true,
      dropdownItems: [
        { name: 'All Matches', href: '/all-matches' },
        { name: 'New Matches', href: '/matches/new' },
        { name: 'Caste Matches', href: '/matches/caste' },
        { name: 'City Matches', href: '/matches/city' },
        { name: 'Premium Matches', href: '/matches/premium' }
      ]
    },
    {
      name: 'Activity Matches',
      icon: Activity,
      hasDropdown: true,
      dropdownItems: [
        { name: 'Who viewed my profile', href: '/matches/viewed-me' },
        { name: 'Who shortlisted me', href: '/activity/shortlisted-me' },
        { name: 'Shortlisted by Me', href: '/activity/my-shortlist' },
        { name: 'Interest sent', href: '/activity/interest-sent' },
        { name: 'Interest received', href: '/activity/interest-received' },
        { name: 'Profiles i viewed', href: '/activity/profiles-viewed' }
      ]
    },
    {
      name: 'Messages',
      icon: MessageCircle,
      href: '/messages',
      hasDropdown: false
    },
    {
      name: 'Search',
      icon: Search,
      hasDropdown: true,
      dropdownItems: [
        { name: 'Advance Search', href: '/search-results' },
        { name: 'Surname Search', action: 'surname' },
        { name: 'ID search', action: 'id' }
      ]
    },
    // Hide membership for premium users
    ...(premium?.isPremium ? [] : [{
      name: 'Membership',
      icon: Crown,
      href: '/membership',
      hasDropdown: false
    }])
  ];

  return (
    <nav className={`bg-gradient-to-br from-primary-50 to-secondary-50 border-b border-gray-200 shadow-sm fixed top-20 left-0 right-0 z-30 hidden md:block ${className}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center h-14">
          <div className="flex items-center gap-[22px] max-[899px]:gap-[14px] max-[899px]:overflow-x-auto max-[899px]:scrollbar-hide max-[899px]:justify-start max-[899px]:w-full max-[899px]:pb-2">
            {navigationItems.map((item) => (
              <div key={item.href ?? item.name} className="relative max-[899px]:flex-shrink-0">
                {item.hasDropdown ? (
                  <div>
                    <button
                      onClick={() => toggleDropdown(item.name)}
                      className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      {item.icon ? <item.icon className="w-4 h-4" /> : null}
                      <span>{item.name}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === item.name ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {activeDropdown === item.name && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-[899px]:left-0 max-[899px]:right-auto">
                        <div className="py-2">
                          {item.dropdownItems?.map((dropdownItem) => (
                            dropdownItem.href ? (
                              <Link
                                key={dropdownItem.name}
                                href={dropdownItem.href}
                                className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors"
                              >
                                <span>{dropdownItem.name}</span>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </Link>
                            ) : (
                              <button
                                key={dropdownItem.name}
                                onClick={() => handleSearchOptionClick(dropdownItem.action!)}
                                className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors text-left"
                              >
                                <span>{dropdownItem.name}</span>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </button>
                            )
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    href={item.href!}
                    className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    {item.isDailyRecommendation ? (
                      <DailyRecommendationMenuLabel />
                    ) : (
                      <>
                        {item.icon ? <item.icon className="w-4 h-4" /> : null}
                        <span>{item.name}</span>
                      </>
                    )}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Backdrop for closing dropdowns when clicking outside */}
      {activeDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setActiveDropdown(null)}
        />
      )}

      {/* Search Popups */}
      <IDSearchPopup 
        isOpen={showIDSearch} 
        onClose={() => setShowIDSearch(false)} 
      />
      <SurnameSearchPopup 
        isOpen={showSurnameSearch} 
        onClose={() => setShowSurnameSearch(false)} 
      />
    </nav>
  );
}
